import axios from 'axios';

// Create an axios instance with default configuration for billing API
const billingClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000',
  timeout: 30000, // 30 seconds timeout
});

// Add a request interceptor to include the authorization token
billingClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle common error responses
billingClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      // Unauthorized - remove token and redirect to login
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Billing API functions
export const billingAPI = {
  // Get current subscription details
  getSubscription: () => billingClient.get('/api/billing/subscription'),

  // Update subscription plan
  updateSubscription: (planId) => billingClient.put('/api/billing/subscription', { planId }),

  // Cancel subscription
  cancelSubscription: () => billingClient.delete('/api/billing/subscription'),

  // Get payment methods
  getPaymentMethods: () => billingClient.get('/api/billing/payment-methods'),

  // Add a new payment method
  addPaymentMethod: (paymentMethod) => billingClient.post('/api/billing/payment-methods', { paymentMethod }),

  // Update default payment method
  updateDefaultPaymentMethod: (paymentMethodId) => billingClient.put('/api/billing/payment-methods/default', { paymentMethodId }),

  // Remove a payment method
  removePaymentMethod: (paymentMethodId) => billingClient.delete(`/api/billing/payment-methods/${paymentMethodId}`),

  // Get recent billing activity (newly added)
  getRecentBillingActivity: () => billingClient.get('/api/billing/recent'),

  // Get billing history/invoices
  getBillingHistory: (limit = 10, offset = 0) => billingClient.get('/api/billing/history', {
    params: { limit, offset }
  }),

  // Get invoice details
  getInvoice: (invoiceId) => billingClient.get(`/api/billing/invoices/${invoiceId}`),

  // Download invoice PDF
  downloadInvoice: (invoiceId) => billingClient.get(`/api/billing/invoices/${invoiceId}/download`, {
    responseType: 'blob'
  }),

  // Get available plans
  getPlans: () => billingClient.get('/api/billing/plans'),

  // Estimate costs for a plan
  estimateCosts: (planId, options = {}) => billingClient.post('/api/billing/estimate', { planId, ...options }),
};

// Canadian tax calculation helper (simplified)
// In a real implementation, this would be handled server-side
export const calculateCanadianTaxes = (amount, province) => {
  // Simplified tax rates for Canadian provinces (HST/GST/PST)
  const taxRates = {
    'AB': 0.05, // Alberta - GST only
    'BC': 0.12, // British Columbia - GST + PST
    'MB': 0.12, // Manitoba - GST + PST
    'NB': 0.15, // New Brunswick - HST
    'NL': 0.15, // Newfoundland and Labrador - HST
    'NS': 0.15, // Nova Scotia - HST
    'ON': 0.13, // Ontario - HST
    'PE': 0.15, // Prince Edward Island - HST
    'SK': 0.11, // Saskatchewan - GST + PST
    'QC': 0.14975, // Quebec - GST + QST
    'NT': 0.05, // Northwest Territories - GST only
    'NU': 0.05, // Nunavut - GST only
    'YT': 0.05, // Yukon - GST only
  };
  
  const rate = taxRates[province] || 0.05; // Default to GST if province not found
  return amount * rate;
};

// Format currency for Canadian dollars
export const formatCAD = (amount) => {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD'
  }).format(amount);
};

// Validate Canadian postal code
export const validateCanadianPostalCode = (postalCode) => {
  const postalCodeRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
  return postalCodeRegex.test(postalCode);
};

// Validate Canadian phone number
export const validateCanadianPhoneNumber = (phoneNumber) => {
  const phoneRegex = /^(\+1\s?)?(\([0-9]{3}\)|[0-9]{3})[\s-]?[0-9]{3}[\s-]?[0-9]{4}$/;
  return phoneRegex.test(phoneNumber);
};

export default billingClient;